import { Component, ViewChild } from '@angular/core';
import { IonicPage, NavController, NavParams, PopoverController, Popover, Content } from 'ionic-angular';

import { BasketPage } from '../basket/basket';
import { DetailPage } from '../detail/detail';
import { SearchPage } from '../search/search';

import { Category } from '../../models/category';
import { Product } from '../../models/product';

import { SortingPopoverPage } from './sorting-popover';

import 'rxjs/add/operator/toPromise';
import { PageBase } from '../page-base';
import { DataProvider } from '../../providers/data/data';
import { BasketProvider } from '../../providers/basket/basket';
import { ScanProvider } from '../../providers/scan/scan';
import { ProductProvider } from '../../providers/product/product';
import { CategoryProvider } from '../../providers/category/category';
import { Sorting, By, Direction } from '../../providers/sorting/sorting';
import { FavoriteProvider } from '../../providers/favorite/favorite';
import { CompanyConfigProvider } from '../../providers/company-config/company-config';

@IonicPage()
@Component({
  selector: 'page-category',
  templateUrl: 'category.html',
})
export class CategoryPage extends PageBase {
  @ViewChild(Content) content: Content;
	
  categoryId: string;
  currentCategory = new Category();
  products: Array<Product> = new Array<Product>();

  page = 0;

  isFavoritesPage: boolean;

  isBusy: boolean = true;

  isFullyLoaded: boolean = false;

  currentSorting = new Sorting(By.DEFAULT, Direction.ASCENDING);

  showPrice: boolean;
  showOrder: boolean;

  constructor(
    private popoverCtrl: PopoverController,
    private productProvider: ProductProvider,
    private categoryProvider: CategoryProvider,
    private basket: BasketProvider,
    private scan: ScanProvider,
    private navCtrl: NavController,
    private navParams: NavParams,
    private favoriteProvider: FavoriteProvider,
    private companyConfigProvider: CompanyConfigProvider) {

    super();

    this.isFavoritesPage = this.navParams.get("favorites");
    this.categoryId = this.navParams.get("categoryId");

    companyConfigProvider.canSeePrice().then(x => {
      this.showPrice = x;
    });

    companyConfigProvider.canSubmitOrder().then(x => {
      this.showOrder = x;
      this.content.resize();
    });

    if (!this.isFavoritesPage) {
      // Is Page with products from category
      // Set specific category
      this.categoryProvider.getCategory(this.categoryId).then(category => {
        this.currentCategory = category;
      });
      this.loadCategory();
    } else {
      // Is page with favorite products
      this.loadFavorites();
    }
  }

  /**
   * Loads next x products into the product list.
   * @param infiniteScroll
   */
  private loadNewPage(infiniteScroll) {
    this.page++;
    this.productProvider.getProducts({ categoryId: this.categoryId, sort: this.currentSorting, page: this.page }).then(x => {
      if (x.length < 30) {
        this.isFullyLoaded = true;
      }
      // Set quantity for each product
      x.forEach(p => p.quantity = this.basket.getProductQuantity(p.uuid));
      // Add each product to list
      x.forEach(p => this.products.push(p));
      infiniteScroll.complete();
    });
  }

  /**
   * Loads all products for this category.
   */
  private loadCategory() {
    this.productProvider.getProducts({ categoryId: this.categoryId, page: this.page }).then(x => {
      if (x.length < 30) {
        this.isFullyLoaded = true;
      }
      // Set product quantity for each product
      x.forEach(p => p.quantity = this.basket.getProductQuantity(p.uuid));
      this.products = x;
      this.isBusy = false;
    });
  }

  /**
   * Loads all products which are marked favorite.
   */
  private loadFavorites() {
    this.favoriteProvider.getFavorites().then(async x => {
      for (var index = 0; index < x.length; index++) {
        var element = x[index];
        // Get product for id
        var p = await this.productProvider.getProduct(element);
        if (p) {
          // Set product quantity for each product
          p.quantity = this.basket.getProductQuantity(p.uuid)
          this.products.push(p);
        } else {
          this.favoriteProvider.removeProduct(element);
        }
      }
      this.isBusy = false;
    });
  }

  /**
  * Sorts products with specified algorithm.
  */
  private async sort(sorting: Sorting) {
    // Update sorting
    this.currentSorting.by = sorting.by;
    this.currentSorting.direction = sorting.direction;
    // Empty lists
    this.products = new Array<Product>();
    this.isBusy = true;
    // Reset page
    this.page = 0;
    this.productProvider.getProducts({ categoryId: this.categoryId, sort: sorting, page: this.page }).then(x => {
      // Set product quantity for each product
      x.forEach(p => p.quantity = this.basket.getProductQuantity(p.uuid));
      this.products = x;
      this.isBusy = false;
    });
  }

  /**
  * Shows popover for selecting sorting algorithm.
  */
  private sortPopover(event) {
    var sortingPopover = this.popoverCtrl.create(SortingPopoverPage, {
      'cb': (x: string) => {
        this.sort(Sorting.fromValue(x));
      },
      'current': this.currentSorting.toValue()
    });
    sortingPopover.present({
      'ev': event,
      animate: false
    });
  }

  // Id of product of which a detail page was shown
  detailId: string;

  /** When the product detail page was popped the quantity of that product must
  *   be updated.
  *   All products are reloaded if the page type is category.
  */
  ionViewWillEnter() {
    if (this.detailId) {
      if (this.isFavoritesPage) {
        this.products = this.products.filter(x => false);
        this.loadFavorites();
      } else {
        var p = this.products.find(x => x.uuid == this.detailId);
        p.quantity = this.basket.getProductQuantity(p.uuid);
      }
    }
  }

  /**
  * Opens barcode scanner.
  */
  private scanBarcode() {
    this.scan.findProduct((item: Product) => {
      this.openDetail(item);
    })
  }

  /**
  * Pushes search page on navigation stack.
  */
  private openSearchPage() {
    this.navCtrl.push(SearchPage);
  }

  /**
  * Pushes detail for specified product id.
  */
  private openDetail(item: Product) {
    this.navCtrl.push(DetailPage, { "product": item });
    this.detailId = item.uuid;
  }

  /**
  * Decreases the quantity of the specified product if the quantity is not 0
  * and updates totals.
  */
  private removeProduct(item: Product) {
    if (item.quantity > 0) {
      item.quantity--;
      // Decrease quantity in basket
      this.basket.decreaseProductQuantity(item.uuid);
    }
  }

  /**
  * Increases the quantity of the specified product and updates totals.
  */
  private addProduct(item: Product) {
    item.quantity++;
    // Increase quantity in basket
    this.basket.increaseProductQuantity(item.uuid);
  }

  /**
  * Shows a popup for setting the quantity for the specified product.
  */
  private setQuantity(item: Product) {
    var q = "";
    if (item.quantity > 0) {
      q = item.quantity.toString();
    }
    this.alertProvider.showPrompt(item.name, q, (data) => {
      var qty: number = +data.quantity;
      if (qty > -1) {
        item.quantity = +data.quantity;
        this.basket.setProductQuantity(item.uuid, +data.quantity);
      }
    });
  }

  /**
  * Navigates to the basket page.
  */
  private navigateToBasket() {
    this.navCtrl.push(BasketPage);
  }
}



// WEBPACK FOOTER //
// ./src/pages/category/category.ts